import React, { useState } from "react";
import { shortenUrl } from "../services/api";

function isValidUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export default function LandingPage() {
  const [url, setUrl] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value.trim();
    setUrl(v);
    setIsValid(isValidUrl(v));
    setError(null);
    setCopied(false);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setCopied(false);
    try {
      const short = await shortenUrl(url);
      setResult(short);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Fehler beim Kürzen der URL";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function copyResult() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Hauptinhalt zentriert */}
      <div className="flex flex-col items-center justify-center px-4 pt-32 pb-16">
        <div className="w-full max-w-4xl">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-5xl sm:text-6xl font-bold tracking-tight text-gray-900 mb-4">
              Verkürze deine Links{" "}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                blitzschnell
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Erstelle kurze, teilbare Links in Sekunden. Verfolge Klicks,
              analysiere Performance und optimiere deine Reichweite.
            </p>
          </div>

          {/* URL Shortener Form */}
          <div className="bg-white shadow-xl ring-1 ring-gray-200 rounded-2xl p-8 mb-12 max-w-3xl mx-auto">
            <form onSubmit={onSubmit}>
              <label
                htmlFor="url"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Füge deine lange URL ein
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  id="url"
                  name="url"
                  type="url"
                  inputMode="url"
                  placeholder="https://www.example.com/very-long-url..."
                  value={url}
                  onChange={onChange}
                  aria-invalid={!isValid && url.length > 0}
                  className="flex-1 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none px-4 py-4 sm:py-3 text-gray-900 text-base placeholder:text-gray-400"
                />
                <button
                  type="submit"
                  disabled={!isValid || loading}
                  className={`px-8 py-3 rounded-xl text-white font-medium ${
                    !isValid || loading
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  } transition-all duration-200`}
                >
                  {loading ? "Kürze..." : "Verkürzen"}
                </button>
              </div>
              {error && (
                <div
                  role="alert"
                  className="mt-3 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm"
                >
                  {error}
                </div>
              )}
            </form>

            {result && (
              <div
                className="mt-6 pt-6 border-t border-gray-200"
                aria-live="polite"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Dein kurzer Link</p>
                    <a
                      href={result}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 block text-lg font-semibold text-blue-700 break-all"
                    >
                      {result}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={copyResult}
                      className="rounded-lg bg-gray-900 text-white px-4 py-2 text-sm font-medium hover:bg-black"
                    >
                      Kopieren
                    </button>
                    {copied && (
                      <span className="text-xs text-green-600">✓ Kopiert!</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Kürzlich verkürzte Links */}
          <div className="bg-white shadow-lg ring-1 ring-gray-200 rounded-2xl p-8 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Kürzlich verkürzte Links
            </h2>
            <div className="space-y-4">
              {/* Beispiel Link 1 */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 mb-1">
                    linky.sh/abc123
                  </div>
                  <div className="text-sm text-gray-500 truncate">
                    https://www.example.com/very-long-url-that-needs-shortening
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">42</div>
                    <div className="text-xs text-gray-500">Klicks</div>
                  </div>
                  <button className="p-3 hover:bg-gray-200 rounded-lg transition-colors h-12 w-12 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Beispiel Link 2 */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 mb-1">
                    linky.sh/gh456
                  </div>
                  <div className="text-sm text-gray-500 truncate">
                    https://github.com/user/repository
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">18</div>
                    <div className="text-xs text-gray-500">Klicks</div>
                  </div>
                  <button className="p-3 hover:bg-gray-200 rounded-lg transition-colors h-12 w-12 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
